import type { ReactNode } from 'react';
import type { PopoverPlacement } from './popover-position';

/** The one shared hover popover: what it shows and for which trigger. */
export interface PopoverState {
    content: ReactNode;
    placement: PopoverPlacement;
    /** The element it points at; undefined before anything has shown. */
    triggerElement: HTMLElement | undefined;
    isVisible: boolean;
    isPinnable: boolean;
    isPinned: boolean;
}

/**
 * The shared popover as a small external store, so triggers can subscribe to
 * just the part they show (their own visibility) instead of re-rendering on
 * every change through context. Every action a trigger calls names that
 * trigger and does nothing to another trigger's popover.
 */
export interface PopoverStore {
    getState: () => PopoverState;
    subscribe: (onStateChange: () => void) => () => void;
    /** Shows `content` for `triggerElement`, unless another trigger's popover is pinned. */
    show: (content: ReactNode, triggerElement: HTMLElement, placement?: PopoverPlacement, isPinnable?: boolean) => void;
    /** Hides the popover if `triggerElement` is showing it and it isn't pinned. */
    hide: (triggerElement: HTMLElement) => void;
    /** Pins the popover open if `triggerElement` is showing it. */
    pin: (triggerElement: HTMLElement) => void;
    /** Unpins and hides the popover, whoever showed it (Escape, the pin button). */
    unpin: () => void;
    /** Hides the popover, pinned or not, if `triggerElement` is showing it (e.g. as that trigger unmounts). */
    release: (triggerElement: HTMLElement) => void;
}

const INITIAL_POPOVER_STATE: PopoverState = {
    content: null,
    placement: 'top',
    triggerElement: undefined,
    isVisible: false,
    isPinnable: false,
    isPinned: false,
};

function isShownFor(state: PopoverState, triggerElement: HTMLElement): boolean {
    return state.isVisible && state.triggerElement === triggerElement;
}

export function createPopoverStore(): PopoverStore {
    let state = INITIAL_POPOVER_STATE;
    const listeners = new Set<() => void>();
    const update = (resolve: (previousState: PopoverState) => PopoverState): void => {
        const nextState = resolve(state);
        if (nextState === state) {
            return;
        }
        state = nextState;
        listeners.forEach((listener) => listener());
    };

    return {
        getState: (): PopoverState => state,
        subscribe: (onStateChange): (() => void) => {
            listeners.add(onStateChange);
            return (): void => {
                listeners.delete(onStateChange);
            };
        },
        show: (content, triggerElement, placement = 'top', isPinnable = false): void => {
            update((previousState) => {
                const isPinnedElsewhere = previousState.isVisible && previousState.isPinned && previousState.triggerElement !== triggerElement;
                if (isPinnedElsewhere) {
                    return previousState;
                }
                return {
                    content,
                    placement,
                    triggerElement,
                    isVisible: true,
                    isPinnable,
                    isPinned: isShownFor(previousState, triggerElement) && previousState.isPinned,
                };
            });
        },
        hide: (triggerElement): void => {
            update((previousState) => (
                isShownFor(previousState, triggerElement) && !previousState.isPinned
                    ? { ...previousState, isVisible: false }
                    : previousState
            ));
        },
        pin: (triggerElement): void => {
            update((previousState) => (
                isShownFor(previousState, triggerElement) && !previousState.isPinned
                    ? { ...previousState, isPinned: true }
                    : previousState
            ));
        },
        unpin: (): void => {
            update((previousState) => (
                previousState.isVisible || previousState.isPinned
                    ? { ...previousState, isPinned: false, isVisible: false }
                    : previousState
            ));
        },
        release: (triggerElement): void => {
            update((previousState) => (
                isShownFor(previousState, triggerElement)
                    ? { ...previousState, isPinned: false, isVisible: false }
                    : previousState
            ));
        },
    };
}
